import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { eventNames, reportToSegment, types } from '@smartcar/morse';
import { Box, Button, FormControl, IconButton, InputLabel, Typography } from '@mui/material';
import { bool, func, instanceOf, number, objectOf, shape, string } from 'prop-types';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import _ from 'lodash';
import validator from 'validator';

import { DateRangePicker, FeatureGateBanner, FiltersForm, InputField, MenuItemWrapper, Select } from '../../../../../../../../components';
import { selectFilters, staticText } from '../../../../../../../../localization/Application/Logs/webhooksFiltersBar';
import getFilterStatusAndTitle from '../../../../../Logs/utils/getFilterStatusAndTitle';
import { AllFiltersDrawer } from './components';
import { errors } from '../../../../../../../../localization/Application/Logs/logs';

const FiltersBar = ({
  filterValues,
  logRestrictions,
  handleApplyFilters,
  handleResetAll,
  setFormError,
  canWriteBilling,
  openFiltersDrawer,
  setOpenFiltersDrawer,
}) => {
  const [newFormState, setNewFormState] = useState(filterValues);
  const {
    logDays, minDate, maxDate, isMaxLogDays,
  } = logRestrictions;
  const dateRange = [newFormState.start, newFormState.end];
  const [vehicleIdFocused, setVehicleIdFocused] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleApplyFilters(newFormState);
    reportToSegment(
      types.TRACK,
      eventNames.formSubmitted,
      {
        label: '[logs - webhooks] filter values',
        form_content: newFormState,
      },
    );
  };

  const handleResetClick = () => {
    setFormError('');
    handleResetAll();
    setNewFormState(filterValues);
    reportToSegment(
      types.TRACK,
      eventNames.buttonClicked,
      {
        label: 'reset',
        text: `[logs - webhooks] ${staticText.reset}`,
      },
    );
  };

  const getHandleChange = filterKey => (e) => {
    if (filterKey === 'dateRange') {
      const [startDate, endDate] = e;
      setNewFormState({
        ...newFormState,
        start: startDate,
        end: endDate,
      });
    } else if (filterKey === 'statusCodes') {
      setNewFormState({
        ...newFormState,
        [filterKey]: e,
      });
    } else {
      setNewFormState({
        ...newFormState,
        [filterKey]: e.target.value,
      });
    }
    setFormError('');
  };
  const logAllowance = staticText.logDaysAllowance(logDays);
  const upgradeMessage = `${logAllowance} ${staticText.featureGated.upgrade}`;
  const ownerUpgradeText = `${logAllowance} ${staticText.featureGated.contactOwnersToUpgrade}`;

  const dateRangeMessaging = isMaxLogDays
    ?
      <Typography>{staticText.maxLogDays(logDays)}</Typography>
    :
    (
      <FeatureGateBanner
        canWriteBilling={canWriteBilling}
        upgradeText={upgradeMessage}
        ownerUpgradeText={ownerUpgradeText}
      />
    );

  const createDateFilter = ({ id, fullWidth }) => (
    <Box display="flex" flexDirection="column">
      {/* TODO consider moving this label to the DRP component */}
      <InputLabel htmlFor={id} required shrink>
        {staticText.dateRange}
      </InputLabel>
      <DateRangePicker
        id={id}
        fullWidth={fullWidth}
        onAccept={getHandleChange('dateRange')}
        value={dateRange}
        minDate={minDate}
        maxDate={maxDate}
        showIcon
        customActionBar={dateRangeMessaging}
      />
    </Box>
  );

  const createVehicleIdFilter = ({ id, width, showErrorMessage = false }) => {
    const value = (newFormState.vehicleId || '').trim();
    const error = value && !validator.isUUID(value) && errors.invalidVehicleId;
    const errorMessage = !vehicleIdFocused && error;
    return (
      <InputField
        inputName={id}
        label={staticText.vehicleId}
        inputPlaceholder={staticText.enterVehicleId}
        handleChange={getHandleChange('vehicleId')}
        inputValues={newFormState.vehicleId}
        customInputWidth={width}
        fullWidth={!width}
        handleBlur={() => setVehicleIdFocused(false)}
        handleFocus={() => setVehicleIdFocused(true)}
        {...showErrorMessage ? {
          errorMessage,
        } : {
          error: !!errorMessage,
        }}
      />
    );
  };

  const createWebhookIdOrNameFilter = ({ id, width }) => (
    <InputField
      inputName={id}
      label={staticText.webhookIdOrName}
      inputPlaceholder={staticText.enterWebhookIdOrName}
      handleChange={getHandleChange('webhookIdOrName')}
      inputValues={newFormState.webhookIdOrName}
      customInputWidth={width}
      fullWidth={!width}
    />
  );

  const createSelectFilters = filters => (
    filters.map(({
      filterKey, label, id, minWidth, placeholder, type, options,
    }) => {
      if (type === 'select') {
        const labelId = `${id}-label`;
        return (
          <FormControl
            key={id}
            variant="outlined"
          >
            <Box display="flex" flexDirection="column">
              <InputLabel id={labelId}>{label}</InputLabel>
            </Box>
            <Select
              id={id}
              name={filterKey}
              labelId={labelId}
              options={options}
              value={newFormState[filterKey]}
              onChange={getHandleChange(filterKey)}
              placeholderOptionLabel={placeholder}
              selectStyles={{ width: minWidth || '100%' }}
            />
          </FormControl>
        );
      }

      const {
        isFilterActive,
        alternativeItemWrapperTitle,
      } = getFilterStatusAndTitle(newFormState, filterKey, placeholder);

      return (
        <Box minWidth={minWidth}>
          <MenuItemWrapper
            name={alternativeItemWrapperTitle}
            label={label}
            isFilterActive={isFilterActive}
            id={id}
            fullWidth
            filterKey={filterKey}
          >
            <FiltersForm
              checkboxOptions={newFormState[filterKey]}
              updateCheckboxes={getHandleChange(filterKey)}
              title={label}
            />
          </MenuItemWrapper>
        </Box>
      );
    })
  );

  const submitButton = (
    <Button
      id="apply-filters-button"
      color="primary"
      disabled={_.isEqual(newFormState, filterValues)}
      type="submit"
      variant="outlined"
      size="small"
    >
      {staticText.apply}
    </Button>
  );

  const createResetButton = (overrides = {}) => (
    <Button
      id="reset-all-button"
      onClick={handleResetClick}
      variant="text"
      size="small"
      {...overrides}
    >
      {staticText.reset}
    </Button>
  );

  useEffect(() => {
    // because filterValues can be changed elsewhere
    setNewFormState(filterValues);
  }, [filterValues]);

  return (
    <form
      onSubmit={handleSubmit}
    >
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="flex-end"
        mb={1}
        gap={1}
      >
        {createDateFilter({ id: 'date-range-picker' })}
        {createSelectFilters(selectFilters)}
        {createVehicleIdFilter({
          id: 'vehicle-id-filter',
          width: '240px',
        })}
        {createWebhookIdOrNameFilter({
          id: 'webhook-id-or-name-filter',
          width: '240px',
        })}
        <IconButton onClick={() => setOpenFiltersDrawer(!openFiltersDrawer)}>
          <TuneRoundedIcon />
        </IconButton>
        {openFiltersDrawer && (
          <AllFiltersDrawer
            closeDrawer={() => setOpenFiltersDrawer(false)}
            createDateFilter={createDateFilter}
            submitButton={submitButton}
            resetButton={createResetButton({ variant: 'outlined', color: 'secondary' })}
            handleSubmit={handleSubmit}
            createSelectFilters={createSelectFilters}
            createVehicleIdFilter={createVehicleIdFilter}
            createWebhookIdOrNameFilter={createWebhookIdOrNameFilter}
          />
        )}
        {submitButton}
        {createResetButton()}
      </Box>
    </form>
  );
};

export default FiltersBar;

FiltersBar.propTypes = {
  filterValues: shape({
    start: instanceOf(moment),
    end: instanceOf(moment),
    statusCodes: objectOf(bool),
  }).isRequired,
  logRestrictions: shape({
    minDate: string.isRequired,
    maxDate: string.isRequired,
    logDays: number.isRequired,
    isMaxLogDays: bool.isRequired,
  }).isRequired,
  handleApplyFilters: func.isRequired,
  handleResetAll: func.isRequired,
  setFormError: func.isRequired,
  canWriteBilling: bool.isRequired,
  openFiltersDrawer: bool.isRequired,
  setOpenFiltersDrawer: func.isRequired,
};
